import { FC, useContext, useState } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { isLeft } from "fp-ts/lib/Either";
import { useForm } from "react-hook-form";
import LocalizationContextType from "../../../../../domain/providers/localization/LocalizationContextType";
import ModalsContextType from "../../../../../domain/providers/modal/ModalsContextType";
import LocalizationContext from "../../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../../providers/modal/ModalsContext";
import Validators from "../../../../utils/Validators";
import CreateQrsComponentProps from "./create_qrs.component.props";
import di from "../../../../../DependencyInjection";
import ButtonComponent from "../../../../components/button/ButtonComponent";
import EntitiesConstants from "../../../../utils/EntitiesConstants";
import SearchStablishmentsUseCase, { SearchStablishmentsUseCaseName } from "../../../../../domain/use_cases/stablishment/SearchStablishmentsUseCase";
import StablishmentEntity from "../../../../../domain/entities/StablishmentEntity";
import AutoCompleteWithCheckComponent from "../../../../components/autocompleteWithCheck/AutoCompleteWithCheckComponent";
import { AutoCompleteItem } from "../../../../components/autocompleteWithCheck/AutoCompleteWithCheckProps";
import CreateQrsInBatchUseCase, { CreateQrsInBatchUseCaseName } from "../../../../../domain/use_cases/qr/CreateQrsInBatchUseCase";

const CreateQrsComponent: FC<CreateQrsComponentProps> = ({
    onCreated,
}) => {
    const formInfo = useForm();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = formInfo;
    const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
    const { addToast } = useContext(ModalsContext) as ModalsContextType;
    const [loading, setLoading] = useState<boolean>(false);

    const _onSearchStablishments = async (query: string): Promise<AutoCompleteItem<StablishmentEntity>[]> => {
        const response = await di.get<SearchStablishmentsUseCase>(SearchStablishmentsUseCaseName).call({
            query, page: 1,
            itemsPerPage: 20,
        });
        if (isLeft(response)) return [];
        const parsed = response.right.data.map((item) => {
            return {
                label: `${item.name} - ${item.address}`,
                id: item.id,
                aditionalValue: item,
            };
        });
        return parsed;
    };

    const _handleCreateQrs = async (data: any) => {
        setLoading(true);

        const stablishmentItem = data[EntitiesConstants.QrBatchEntityStablishment]?.[0];

        if (!stablishmentItem) {
            addToast(i18n("Debe seleccionar un establecimiento"), "error", undefined);
            setLoading(false);
            return;
        }

        const quantity = parseInt(data.quantity);
        if (quantity <= 0 || quantity > 500) {
            addToast(i18n("La cantidad debe ser entre 1 y 500"), "error", undefined);
            setLoading(false);
            return;
        }

        const response = await di
            .get<CreateQrsInBatchUseCase>(CreateQrsInBatchUseCaseName)
            .call({
                quantity: quantity,
                stablishmentId: stablishmentItem.id,
            });

        if (isLeft(response)) {
            addToast(i18n("Error al crear QRs"), "error", undefined);
            setLoading(false);
            return;
        }

        addToast(i18n(`${quantity} QRs creados exitosamente`), "success", undefined);
        onCreated?.();
        setLoading(false);
    };

    return (
        <form
            className="row"
            onSubmit={handleSubmit(_handleCreateQrs)}
        >
            <div
                className={`form-group pt-2 col-md-12 my-2 ${errors[EntitiesConstants.QrBatchEntityStablishment] &&
                    "error error_group"
                    }`}
            >
                <AutoCompleteWithCheckComponent
                    multiple={false}
                    keyName={EntitiesConstants.QrBatchEntityStablishment}
                    label={i18n("Establecimiento")}
                    formInfo={formInfo}
                    onSearch={_onSearchStablishments}
                />
            </div>

            <div
                className={`form-group col-md-12 my-3 ${errors.quantity && "error error_group"}`}
            >
                <label className="form-label">
                    {i18n("Cantidad de QRs")}
                </label>
                <input
                    type="number"
                    min="1"
                    disabled={loading}
                    className="form-control"
                    {...register(
                        "quantity",
                        Validators({ required: true, minValue: 1, maxValue: 1000 })
                    )}
                />
                <ErrorMessage
                    as="aside"
                    errors={errors}
                    name="quantity"
                />
            </div>

            <div className="w-100 d-flex justify-content-end my-3">
                <ButtonComponent
                    isLoading={loading}
                    icon={<span className="material-symbols-outlined">add</span>}
                    type="submit"
                    design="primaryOutline"
                    text={i18n("Crear QRs")}
                />
            </div>
        </form>
    );
};

export default CreateQrsComponent;